// External libraries
// @ts-expect-error
import { isObject, isString } from '@checkout/react-components';

// Internal modules
import type { ValueOf } from '@/types';
import { logger } from '../logger';
import { getGeoRegion, getGlobals, getLocale } from '../globals';
import { isDev } from '../utils/env';
import type { ConfigKey, ConfigType } from './config';
import type { FeaturesType } from './features';
import type { GlobalsType } from '@/lib/globals';

interface GetPropOrDefaultBaseParam<T extends ConfigKey> {
  config: ConfigType;
  featureName: T;
}

type GetPropOrDefault = {
  <T extends ConfigKey, K extends keyof ConfigType[T]>({
    config,
    featureName,
    propNames,
  }: { propNames: K } & GetPropOrDefaultBaseParam<T>):
    | ConfigType[T][K]
    | null
    | undefined;
  <T extends ConfigKey, K extends (keyof ConfigType[T])[]>({
    config,
    featureName,
    propNames,
  }: { propNames: K } & GetPropOrDefaultBaseParam<T>):
    | ConfigType[T][K[number]]
    | null
    | undefined;
};

const getPropOrDefault: GetPropOrDefault = <
  T extends ConfigKey,
  K extends keyof ConfigType[T] | (keyof ConfigType[T])[],
>({
  config,
  featureName,
  propNames,
}: { propNames: K } & GetPropOrDefaultBaseParam<T>):
  | ValueOf<ConfigType[T]>
  | null
  | undefined => {
  const featureConfig = config[featureName];

  if (!isObject(featureConfig)) {
    logger.warn(`feature_${featureName}_not_found_on_config`);
    return null;
  }

  const keys: (keyof ConfigType[T] | 'DEFAULT')[] = [
    ...(Array.isArray(propNames) ? propNames : [propNames]),
    'DEFAULT',
  ];

  const featureKey = keys.find(key => featureConfig.hasOwnProperty(key));

  // @ts-expect-error - We know 'DEFAULT' may not be in it
  const featureValue = featureKey ? featureConfig[featureKey] : undefined;

  logger.info('client_feature_resolution', {
    featureKey,
    featureName,
    featureValue,
  });

  return featureValue;
};

// Defining return type is huge and haven't found a way to pass generic
const getCountryConfigValue = <T extends ConfigKey>(
  featureName: T,
  config: ConfigType,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
) => {
  const { country } = getLocale();
  const geoRegion = getGeoRegion();

  return getPropOrDefault({
    config,
    featureName,
    // @ts-expect-error
    // We may get back undefined, but not altering prod code for TS migration
    propNames: [country, geoRegion],
  });
};

const getExperiment = <T extends keyof GlobalsType['experiments']>(
  featureName: T,
): GlobalsType['experiments'][T] => {
  return getGlobals().experiments[featureName];
};

// TODO: Return type will be know when globalUtils formatter is typed
const getFactors = (featureName: string): any => {
  const feature = getExperiment(featureName);
  return feature?.factors || null;
};

const getTreatment = (featureName: string): string | null => {
  const feature = getExperiment(featureName);
  return feature?.treatmentName || null;
};

const hasExactTreatment = ({
  feature,
  treatment,
}: {
  feature: string;
  treatment: string;
}): boolean => {
  const treatmentName = getTreatment(feature);
  return Boolean(treatmentName) && treatmentName === treatment;
};

const hasTreatment = (feature: string): boolean => {
  const treatment = getTreatment(feature);
  return isString(treatment) && treatment?.startsWith('Trmt_');
};

const includesTreatment = ({
  feature,
  treatment,
}: {
  feature: string;
  treatment: string;
}): boolean => {
  const treatmentName = getTreatment(feature);
  return isString(treatmentName) && treatmentName?.includes(treatment);
};

/**
 * @Example
 * feature - FEATURE_POMA_BUTTON_CONTENT
 * if the feature has treatment name as
 * Trmt_FEATURE_POMA_BUTTON_CONTENT_CONTINUE_AND_CREATE_ACCOUNT
 * then returned variant will be - CONTINUE_AND_CREATE_ACCOUNT
 */
const getVariantFromTreatmentName = ({
  feature,
  defaultVariant = '',
}: {
  feature: string;
  defaultVariant?: string;
}): string => {
  const treatmentName = getTreatment(feature) || '';
  const [, variant = ''] = treatmentName.split(`_${feature}_`);
  return variant || defaultVariant;
};

/**
 * Need to maintain the order in which the checks are made, otherwise
 * we will be logging wrong values to FPTI
 */
const getFptiFlowName = ({
  clientFeatures = {},
}: {
  clientFeatures: Partial<FeaturesType['config']>;
}): string =>
  (clientFeatures.isWpsGuestUpgradeEligible &&
    !clientFeatures.isPOMAEligible &&
    'WPSGuestUpgrade') ||
  (clientFeatures.isWps &&
    clientFeatures.isOptionalSignup &&
    'WPSOptionalGuest') ||
  (clientFeatures.isWps &&
    clientFeatures.isForcedSignUp &&
    'WPSForcedSignup') ||
  (clientFeatures.isForcedGuest && 'ForcedGuest') ||
  (clientFeatures.isOptionalSignup && 'OptionalGuest') ||
  (clientFeatures.isPOMAEligible && 'PasswordlessOptionalGuest') ||
  (clientFeatures.passwordlessForcedSignup && 'PasswordlessForcedSignup') ||
  (clientFeatures.isForcedSignUp && 'ForcedSignup') ||
  'unknown';

const flowToFptiPageName = (flow: string): string =>
  `${flow}ApproveAndReturnToMerchant`;

/**
 * @description if the kycConfigKey is a function it will
 * call it with the nationality param, resolving its values
 * @param {any} kycConfigKey: a value or a function
 * @param {string} nationality: country
 * @returns resolved value (for KycTypes is an array of types)
 */
type GetNationalityBasedKycValue = {
  <K extends string, T extends (param: K) => any>(
    kycConfigKey: T,
    nationality: K,
  ): ReturnType<T>;
  <K extends string, T extends string>(kycConfigKey: T, nationality: K): string;
};

const getNationalityBasedKycValue: GetNationalityBasedKycValue = <
  K extends string,
  T extends ((param: K) => any) | string,
>(
  kycConfigKey: T,
  nationality: K,
) => {
  if (typeof kycConfigKey === 'function') {
    return kycConfigKey(nationality);
  }
  return kycConfigKey;
};

// Make it easy to check Elmo settings on console in dev
if (isDev) {
  // @ts-expect-error - dev tools
  global.elmo = {
    allExperiments: getGlobals().experiments,
    getCountryConfigValue,
    getExperiment,
    getFactors,
    getFptiFlowName,
    getNationalityBasedKycValue,
    getPropOrDefault,
    getTreatment,
    getVariantFromTreatmentName,
    hasExactTreatment,
    hasTreatment,
    includesTreatment,
  };
}

export {
  flowToFptiPageName,
  getCountryConfigValue,
  getExperiment,
  getFactors,
  getFptiFlowName,
  getNationalityBasedKycValue,
  getPropOrDefault,
  getTreatment,
  getVariantFromTreatmentName,
  hasExactTreatment,
  hasTreatment,
  includesTreatment,
};
